// InkProtocol - English Academic Writing
// Histoire : Un scandale de plagiat secoue une universite prestigieuse

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface InkProtocolScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const inkProtocolCharacters: Character[] = [
  { id: 'viktor', name: 'Prof. Viktor Novak', role: 'Professor',    color: '#1E3A5F', avatar: 'viktor' },
  { id: 'olga',   name: 'Dr. Olga Smirnova', role: 'Editor',        color: '#4A1942', avatar: 'olga'   },
  { id: 'kenji',  name: 'Kenji Tanaka',       role: 'PhD Student',   color: '#1B4332', avatar: 'kenji'  },
];

// Chapitre I : The Accusation

const scene1: SequenceItem = {
  type: 'story',
  id: 'ip-scene-1',
  title: 'Scene 1 : The ethics committee letter',
  duration: 4,
  content: {
    narrativeText:
      "Faculty of Computational Linguistics, Hartwell University. Monday, 9:14 AM.\n\n" +
      "**Prof. Viktor Novak :** \"Olga. I just received a formal notice from the Ethics Committee. They are opening an investigation into my paper on neural discourse analysis.\"\n\n" +
      "**Dr. Olga Smirnova :** \"Which paper? The one published in the Journal of Computational Pragmatics last spring?\"\n\n" +
      "**Prof. Viktor Novak :** \"Yes. Someone filed a complaint alleging that sections of my literature review overlap with Kenji Tanaka's unpublished thesis draft. Sections that were shared with me for peer feedback.\"\n\n" +
      "**Kenji Tanaka :** \"Professor, I did not file the complaint. But I have to be honest — when I read your published paper, I recognized my own phrasing in three paragraphs.\"\n\n" +
      "**Prof. Viktor Novak :** \"Those paragraphs describe established concepts, Kenji. Anyone working in this field would use similar terminology.\"\n\n" +
      "**Dr. Olga Smirnova :** \"Viktor, intent is irrelevant to the committee. What matters is whether the overlap constitutes plagiarism under the APA citation standards the journal uses.\"\n\n" +
      "**Kenji Tanaka :** \"What are the actual definitions? When does paraphrase become plagiarism?\"\n\n" +
      "**Dr. Olga Smirnova :** \"That is exactly what we need to understand before the hearing. Let us start from the fundamentals of academic citation.\"",
    characters: ['viktor', 'olga', 'kenji'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'ip-card-1',
  title: 'Concept : Academic Integrity & Citation Styles',
  duration: 5,
  content: {
    title: 'Academic Integrity — Citation Styles & Plagiarism',
    body:
      "## Citation Styles Overview\n\n" +
      "| Style | Field | Key Format | Example |\n" +
      "|---|---|---|---|\n" +
      "| **APA 7th** | Social sciences, psychology | Author, Year | (Tanaka, 2023, p. 45) |\n" +
      "| **MLA 9th** | Humanities, literature | Author Page | (Tanaka 45) |\n" +
      "| **Chicago 17th** | History, arts | Footnote/Author-Date | Tanaka (2023) |\n\n" +
      "## Types of Plagiarism\n\n" +
      "1. **Direct plagiarism** — copying text word-for-word without quotation marks and citation\n" +
      "2. **Mosaic plagiarism** — mixing original and copied phrases, even with partial citation\n" +
      "3. **Paraphrase plagiarism** — restating ideas in slightly different words without citation\n" +
      "4. **Self-plagiarism** — reusing your own previously published work without disclosure\n" +
      "5. **Idea theft** — using someone's unpublished ideas without permission or attribution\n\n" +
      "## Proper Paraphrase vs Plagiarism\n\n" +
      "**Original (Tanaka, 2023, p. 12):**\n" +
      "> \"Neural discourse models demonstrate superior coherence tracking compared to rule-based systems.\"\n\n" +
      "**Plagiarism (mosaic):**\n" +
      "Neural discourse models show superior coherence tracking versus rule-based systems.\n\n" +
      "**Correct paraphrase (APA):**\n" +
      "According to Tanaka (2023), deep learning approaches outperform symbolic methods in maintaining discourse coherence.\n\n" +
      "## Key Rule\n" +
      "Any idea, data, or phrasing that is not **common knowledge** and not **your own original thought** requires a citation.",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'ip-chat-1',
  title: 'Discussion: Professor and PhD Student on paper structure',
  duration: 6,
  content: {
    tutorCharacterId: 'olga',
    messages: [
      {
        id: 'ip-chat-1-q1',
        sender: 'character',
        characterId: 'olga',
        text: "Prof. Novak used the phrasing 'neural discourse models demonstrate superior coherence tracking' without citing Kenji's draft. Under APA 7th, is this a citation violation?",
        type: 'single-choice',
        options: [
          "No — it is standard terminology in the field and does not need citation",
          "Yes — any specific claim or characterization from another's work requires attribution, even if paraphrased",
          "Only if the sentence is copied word-for-word",
          "No — since the paper was shared informally for feedback, it is not considered a published source",
        ],
        correctAnswer: "Yes — any specific claim or characterization from another's work requires attribution, even if paraphrased",
        explanation: "Under APA 7th edition, paraphrase requires citation just as a direct quote does. The rule is about the origin of the idea, not the exact wording. Because this specific characterization of neural models originated in Kenji's draft — which was shared with Viktor — failing to cite it constitutes plagiarism regardless of intent.",
      },
      {
        id: 'ip-chat-1-q2',
        sender: 'character',
        characterId: 'olga',
        text: "Kenji is writing a new paper and wants to build on his thesis findings. He rewrites his own paragraph substantially. Does he need to cite his previous work?",
        type: 'single-choice',
        options: [
          "No — it is his own work and he can reuse it freely",
          "Yes — reusing one's own previously published work without disclosure is self-plagiarism",
          "Only if more than 30% of the new paper comes from the old one",
          "Only if the thesis is published in a peer-reviewed journal",
        ],
        correctAnswer: "Yes — reusing one's own previously published work without disclosure is self-plagiarism",
        explanation: "Self-plagiarism occurs when an author reuses substantial portions of their own previous work in a new publication without proper citation or disclosure. Most journals now explicitly prohibit this. The solution is straightforward: cite the previous work and clearly indicate what is new in the current paper.",
      },
      {
        id: 'ip-chat-1-q3',
        sender: 'character',
        characterId: 'olga',
        text: "What is the correct APA 7th in-text citation format for a direct quote on page 45 of Tanaka's 2023 book?",
        type: 'single-choice',
        options: [
          "(Tanaka 45)",
          "(Tanaka, 2023, p. 45)",
          "[Tanaka, 2023, p. 45]",
          "Tanaka (2023: 45)",
        ],
        correctAnswer: "(Tanaka, 2023, p. 45)",
        explanation: "APA 7th edition requires three elements for a direct quote: author's last name, publication year, and page number with 'p.' prefix. The format is (Author, Year, p. Page). For a paraphrase, the page number is recommended but not required. MLA uses (Author Page) without year, which is why style consistency is critical in academic writing.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'ip-task-1',
  title: 'Restructurer un abstract selon les normes APA',
  duration: 20,
  content: {
    platform: 'Academic Writing — APA 7th Edition',
    objective: "Restructurer l abstract du paper de Viktor selon les normes APA pour le soumettre au comite d ethique",
    azurePortalPath: 'Academic Writing Workshop — Hartwell University',
    steps: [
      {
        stepNumber: 1,
        action: "Identify the Context sentence",
        detail: "The abstract must open with 1-2 sentences establishing the research context and the gap in existing knowledge. Example: 'Neural discourse analysis has advanced significantly in recent years, yet coherence tracking in multilingual contexts remains underexplored.'",
      },
      {
        stepNumber: 2,
        action: "State the Method clearly",
        detail: "One concise sentence describing the research design: data source, sample size, and the primary analytical approach. Avoid jargon. Example: 'This study analyzed 1,200 discourse samples using a transformer-based coherence model trained on cross-lingual corpora.'",
      },
      {
        stepNumber: 3,
        action: "Summarize the Results with specifics",
        detail: "Report the most significant quantitative finding. Abstracts with specific numbers are cited 2-3x more often. Example: 'The proposed model achieved 87.3% coherence accuracy, outperforming the baseline by 14.2 percentage points.'",
      },
      {
        stepNumber: 4,
        action: "Write the Conclusion and implications",
        detail: "Close with the practical or theoretical significance. What does this mean for the field? Example: 'These findings suggest that cross-lingual transfer learning can substantially reduce annotation costs for low-resource discourse tasks.'",
      },
      {
        stepNumber: 5,
        action: "Verify APA formatting and citation compliance",
        detail: "Check: abstract is 150-250 words, no citations inside the abstract unless essential, present tense for conclusions and past tense for methods/results, no abbreviations without definition, no first-person ('I' or 'we' should be avoided in APA abstracts).",
      },
    ],
    validation: "Abstract restructured with four sections (context, method, results, conclusion). Word count 150-250. APA 7th formatting verified. No unattributed claims from external sources.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'ip-quiz-1',
  title: 'Quiz: Academic Writing — Citation Rules & Integrity',
  duration: 5,
  content: {
    questions: [
      {
        id: 'ip-q1-1',
        question: "In APA 7th edition, which citation format is correct for paraphrasing a source by Johnson published in 2021?",
        type: 'single-choice',
        options: [
          "(Johnson, 2021)",
          "(Johnson 2021)",
          "[Johnson, 2021]",
          "Johnson (21)",
        ],
        correctAnswer: "(Johnson, 2021)",
        explanation: "APA 7th uses parenthetical citations with author last name and year, separated by a comma: (Johnson, 2021). Page numbers are recommended but not required for paraphrases. MLA uses (Johnson page) without year, and Chicago typically uses footnotes. Mixing styles in a single document is a common error that signals lack of academic rigor.",
      },
      {
        id: 'ip-q1-2',
        question: "What distinguishes 'mosaic plagiarism' from 'direct plagiarism'?",
        type: 'single-choice',
        options: [
          "Mosaic plagiarism only applies to images, not text",
          "Mosaic plagiarism mixes original and copied phrases, making it harder to detect than word-for-word copying",
          "Mosaic plagiarism is not considered a violation if less than 20% of text is borrowed",
          "Direct plagiarism requires intentional deception; mosaic does not",
        ],
        correctAnswer: "Mosaic plagiarism mixes original and copied phrases, making it harder to detect than word-for-word copying",
        explanation: "Mosaic plagiarism (also called patchwriting) involves taking phrases from a source and inserting them into your own sentences, sometimes changing a few words. It is considered plagiarism even if portions are original and even if unintentional. Plagiarism detection software has become adept at identifying these patterns.",
      },
      {
        id: 'ip-q1-3',
        question: "An author submits a new journal article that reuses 40% of text from their own previously published conference paper without disclosure. This is:",
        type: 'single-choice',
        options: [
          "Acceptable — an author always owns their own work",
          "Self-plagiarism, which violates most journal publication ethics policies",
          "Acceptable if the conference paper was not peer-reviewed",
          "Not plagiarism — it only applies to using other people's work",
        ],
        correctAnswer: "Self-plagiarism, which violates most journal publication ethics policies",
        explanation: "Self-plagiarism is a recognized violation because it misrepresents the originality of the submission. When an author publishes in a journal, they typically sign a copyright transfer. Reusing that material elsewhere without disclosure violates both the journal's policy and potentially copyright law. The solution is to cite the prior work and clearly describe what is new.",
      },
    ],
  } as QuizContent,
};

const scene2: SequenceItem = {
  type: 'story',
  id: 'ip-scene-2',
  title: 'Scene 2 : The ruling and the lesson',
  duration: 4,
  content: {
    narrativeText:
      "Ethics Committee Hearing Room, Hartwell University. Three weeks later.\n\n" +
      "**Dr. Olga Smirnova :** \"The committee reviewed Viktor's evidence trail: the annotated email thread, the timestamped draft versions, and the revised paper with proper citations.\"\n\n" +
      "**Prof. Viktor Novak :** \"They accepted the explanation. The overlap was unintentional — I internalized Kenji's phrasing from feedback sessions without tracking the source. The revised paper is now published with the correct attributions.\"\n\n" +
      "**Kenji Tanaka :** \"I appreciate that, Professor. For what it's worth, seeing my ideas properly cited in a published paper is... actually quite meaningful.\"\n\n" +
      "**Dr. Olga Smirnova :** \"This case illustrates something important: most plagiarism is not malicious. It comes from poor note-taking, rushed writing, and a misunderstanding of what requires citation.\"\n\n" +
      "**Prof. Viktor Novak :** \"I have changed my research workflow. Every note I take now includes the source immediately. No note without provenance.\"\n\n" +
      "**Kenji Tanaka :** \"I am implementing the same practice. And I am restructuring my thesis abstract according to the APA framework we discussed.\"\n\n" +
      "**Dr. Olga Smirnova :** \"Good. Remember: in academic writing, precision and attribution are not bureaucratic formalities. They are the infrastructure of trust that makes science possible.\"",
    characters: ['viktor', 'olga', 'kenji'],
  } as StoryContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'The Accusation',
  subtitle: 'Academic Integrity — Citation styles, plagiarism types, and APA writing',
  learningObjectives: [
    "Distinguish between direct plagiarism, mosaic plagiarism, and self-plagiarism",
    "Apply APA 7th edition in-text citation rules for direct quotes and paraphrases",
    "Identify the difference between proper paraphrase and citation violations",
    "Structure an academic abstract using the Context-Method-Results-Conclusion framework",
    "Understand journal ethics policies on self-plagiarism and prior publication disclosure",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1, scene2],
};

export const inkProtocolScenario: InkProtocolScenario = {
  id: 'ink-protocol',
  title: 'The Ink Protocol',
  subtitle: "Les mots sont des armes dans le monde academique",
  certification: 'English',
  characters: inkProtocolCharacters,
  chapters: [chapter1],
};

export default inkProtocolScenario;